import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { IKUserMatch, IPKSettings, IPKUserMatch } from '../pk';
import { BehaviorSubject, Subject } from 'rxjs';
import { PkService } from '../pk.service';
import { FiCustomerDataService } from '../../../shared/fi-shared-services/fi-customer-data.service';
import { FiDialogsService } from '../../../shared/fi-dialogs';
import { FiPkUsersMatchingDialogComponent } from './fi-pk-users-matching-dialog/fi-pk-users-matching-dialog.component';
import { AppTextService } from '../../../shared/app-text';
import { PkMatchType } from '../pk-match-type';
import { FiPkUserMatchDialogComponent } from './fi-pk-user-match-dialog/fi-pk-user-match-dialog.component';
import { takeUntil } from 'rxjs/operators';
import { isDefined, isEqual } from '../../../shared/utils';
import { UserModel } from '../../../user/user.model';
import { ProgressType } from '../../../shared/fi-signal-r/fi-signal-enums';
import { SaveChangesWarningService } from '../../../shared/fi-shared-services/save-changes-warning.service';
import { FiSpinnerService } from '../../../shared/fi-spinner/fi-spinner.service';
import { FiSnackBarService } from '../../../shared/fi-snackbar/snack-bar.service';
import { PKUsersImportType } from '@fi-app/external-platform-integration/pk/pk-users-import-type';

@Component({
    selector: 'pk-users-matching',
    templateUrl: './pk-users-matching.component.html',
    styleUrls: ['./pk-users-matching.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'pk-users-matching' },
    standalone: false,
})
export class PkUsersMatchingComponent implements OnInit, OnDestroy {
    @Input() users: UserModel[];
    @Input() pkSettings: IPKSettings;
    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    pkUserMatches$ = new BehaviorSubject<IPKUserMatch[]>([]);
    isSaveButtonShow = false;
    pkImportTableTypes = PkMatchType;

    alreadyMatchedChainUsers: UserModel[] = [];
    autoMatchedChainUsers: UserModel[] = [];
    notMatchedChainUsers: UserModel[] = [];
    private hasChanges = false;

    constructor(
        private pkService: PkService,
        private customerDataService: FiCustomerDataService,
        private dialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private fiSpinnerService: FiSpinnerService,
        private saveChangesWarningService: SaveChangesWarningService,
        private fiSnackBarService: FiSnackBarService,
    ) {}

    ngOnInit(): void {
        this.subscribeOnPkUserMatchChange();
        this.openMatchUsersDialog();

        this.saveChangesWarningService.start(() => this.hasChanges);
    }

    private openMatchUsersDialog(): void {
        this.dialogsService
            .custom({
                contentComponentRef: FiPkUsersMatchingDialogComponent,
                headerText: this.appTextService.get('pk.matching_users_dialog_header'),
                yesButtonText: this.appTextService.get('pk.matching_users_get_users'),
                config: {
                    autoFocus: false,
                },
            })
            .onYesButtonClick(() => {
                this.getUserMatches();
            })
            .onNoButtonClick(() => {
                window.history.back();
            });
    }

    private getUserMatches(): void {
        this.loading$.next(true);

        const promise = this.pkService
            .getUserMatches()
            .then((result: IPKUserMatch[]) => {
                this.pkUserMatches$.next(result);
                this.isSaveButtonShow = true;
                this.hasChanges = false;
            })
            .finally(() => {
                this.loading$.next(false);
            });

        this.fiSpinnerService.trackPromise(promise, {
            headerText: this.appTextService.get('pk.matching_users_dialog_loading_header'),
            signalRProgressType: ProgressType.PKUserMatch,
        });
    }

    public onUserClick(pkUserMatch: IKUserMatch): void {
        this.openAdjustUserMatchDialog(pkUserMatch);
    }

    private subscribeOnPkUserMatchChange(): void {
        this.pkUserMatches$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((value: IPKUserMatch[]) => {
            const alreadyMatchedPkUsers: IPKUserMatch[] = value.filter((item: IPKUserMatch) => {
                return item.PKMatchType === PkMatchType.PrevMatched;
            });
            this.alreadyMatchedChainUsers = this.getAutoAlreadyMatchedChainUsers(alreadyMatchedPkUsers);

            const autoMatchedPkUsers: IPKUserMatch[] = value.filter((item: IPKUserMatch) => {
                return item.PKMatchType === PkMatchType.AutoMatched;
            });
            this.autoMatchedChainUsers = this.getAutoAlreadyMatchedChainUsers(autoMatchedPkUsers);

            this.notMatchedChainUsers = this.users.filter((user: UserModel) => {
                return (
                    !this.alreadyMatchedChainUsers.find((matchedUser: UserModel) => isEqual(matchedUser, user)) &&
                    !this.autoMatchedChainUsers.find((matchedUser: UserModel) => isEqual(matchedUser, user))
                );
            });
        });
    }

    private getAutoAlreadyMatchedChainUsers(pkUsers: IPKUserMatch[]): UserModel[] {
        return this.users.filter((user: UserModel) => {
            return pkUsers.find((pkUser: IPKUserMatch) => pkUser.UserId === user.Id);
        });
    }

    private openAdjustUserMatchDialog(pkUserMatch: IKUserMatch) {
        this.dialogsService
            .custom({
                contentComponentRef: FiPkUserMatchDialogComponent,
                contentComponentData: {
                    importedUsers: this.pkUserMatches$.value,
                    pkUserMatch,
                    usersImportType: this.pkSettings.UsersImportType,
                },
                headerText: this.appTextService.get('pk.match_user'),
                yesButtonText: this.appTextService.get('done'),
                config: {
                    width: '600px',
                    autoFocus: false,
                },
            })
            .onYesButtonClick((selectedUser: IPKUserMatch) => {
                const pkUsers: IPKUserMatch[] = this.pkUserMatches$.value;
                if (selectedUser) {
                    const currentUserIndex: number = pkUsers.findIndex((pkUser: IPKUserMatch) =>
                        isEqual(pkUser, selectedUser),
                    );
                    if (isDefined(pkUserMatch.pkUser)) {
                        pkUserMatch.pkUser.UserId = undefined;
                        pkUsers[currentUserIndex].PKMatchType = pkUserMatch.pkUser.PKMatchType;
                    } else {
                        pkUsers[currentUserIndex].PKMatchType = PkMatchType.NotMatched;
                    }
                    pkUsers[currentUserIndex].UserId = pkUserMatch.chainformationUser.Id;
                } else if (isDefined(pkUserMatch.pkUser)) {
                    pkUserMatch.pkUser.UserId = undefined;
                    pkUserMatch.pkUser.PKMatchType = PkMatchType.NotMatched;
                }

                this.pkUserMatches$.next(pkUsers);
                this.hasChanges = true;
            });
    }

    saveUserMatches() {
        const promise = this.pkService.saveUserMatches(this.pkUserMatches$.value).then(() => this.getUserMatches());

        this.fiSpinnerService
            .trackPromise(promise, {
                headerText: this.appTextService.get('pk.matching_users_dialog_saving_header'),
                isLoadingProgressHidden: true,
            })
            .then(() => this.showSnackbar());
    }

    private showSnackbar(): void {
        this.fiSnackBarService.showSnackbar({
            textMessage: this.appTextService.get('pk.snackbar_matching_users'),
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
